let _ = t => t,
  _t;
import styled from 'foundations-theming/utils';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { getRootUrl } from 'pricing-pages-lib/utils/urls';
import { breakpoint } from 'pricing-pages-ui-components/constants/Breakpoints';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { AllProductLines } from 'self-service-api/constants/ProductLines';
import PhoneSupport from '../components/common/PhoneSupport';
import { OWNERSHIP_CHECK_FAILED } from '../constants/ErrorCauses';
import { NavItemEnum } from '../constants/routing/NavItems';
import { SCREENS } from '../constants/tracking/Screens';
import { CREATE_A_BUNDLE } from '../constants/tracking/TrackingSources';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import useIsEligibleForHsfxDiscount from '../hooks/useIsEligibleForHsfxDiscount';
import { useTrackRetail } from '../hooks/useTrackRetail';
import { hasOnlyOwnershipCheckFailedSelector, hasPricingAndPurchasingFailedSelector } from '../selectors/LoadingStateSelectors';
import { Flex } from 'foundations-components/components/Flex';
import { Spacer } from 'foundations-components/components/Spacer';
import { PPUIPageHeader } from 'pricing-pages-ui-components/semantic/PageHeader';
import { PageSection } from 'pricing-pages-ui-components/semantic/PageSection';
import { Redirect } from 'react-router-dom';
import { BUNDLE } from 'self-service-api/constants/UpgradeProducts';
import BannerSection from '../components/banners/BannerSection';
import ProductPageSubtitle from '../components/common/ProductPageSubtitle';
import ProductPageTitle from '../components/common/ProductPageTitle';
import FailedLoadBanner from '../components/common/loading/FailedLoadBanner';
import FallbackErrorBanner from '../components/common/loading/FallbackErrorBanner';
import HeaderLayout from '../components/layout/HeaderLayout';
import AddonsPickerList from '../components/plan-builder/AddonsPickerList';
import HubPlanPickerListContainer from '../components/plan-builder/HubPlanPickerList/HubPlanPickerListContainer';
import PlanBuilderContainer from '../components/plan-builder/PlanBuilderContainer';
import SummaryColumn from '../components/plan-builder/SummaryColumn';
import { useGlobalLoading } from '../hooks/loadingHooks';
import { useCountryCode, useDisplayedCurrencyCode } from '../hooks/useCurrencies';
import { isAppAuthSelector } from '../selectors/AuthSelectors';
import { Metrics } from '../utils/metrics';
import { trackPageView } from '../utils/tracking/trackingEventUtils';
import { useQueryParamTerm, useQueryParamsEffect } from '../utils/urls/appQueryParamUtils';
import ErrorPage from './ErrorPage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ContentContainer = styled.main.attrs({
  role: 'main'
}).withConfig({
  displayName: "BundlePage__ContentContainer",
  componentId: "sc-16e1jia-0"
})(["max-width:1150px;width:inherit;"]);
const RightColumnWrapper = styled.div.withConfig({
  displayName: "BundlePage__RightColumnWrapper",
  componentId: "sc-16e1jia-1"
})(["width:400px;margin-top:34px;margin-left:10px;", ";"], breakpoint.cs(_t || (_t = _`
    margin: 0 auto;
  `)));
const BundlePage = () => {
  useTrackRetail();
  useDocumentTitle('bundle');
  const country = useCountryCode();
  const currencyCode = useDisplayedCurrencyCode();
  const eligibleForHsfxDiscount = useIsEligibleForHsfxDiscount();
  const isAppAuth = useSelector(isAppAuthSelector);
  useEffect(() => {
    getUsageTracker().setProperties({
      activeCurrencyCode: currencyCode,
      countryTerritory: country,
      defaultedTab: '',
      tab: '',
      screen: SCREENS.BUNDLE,
      trackingSource: CREATE_A_BUNDLE
    });
    trackPageView.withLastKnownEventProperties({});
    return () => {
      getUsageTracker().setProperties({
        trackingSource: undefined
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const isAppReady = useGlobalLoading();
  const hasPricingAndPurchasingFailed = useSelector(hasPricingAndPurchasingFailedSelector);
  const hasOnlyOwnershipCheckFailed = useSelector(hasOnlyOwnershipCheckFailedSelector);
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  if (hasPricingAndPurchasingFailed) {
    if (hasOnlyOwnershipCheckFailed) {
      return /*#__PURE__*/_jsx(ErrorPage, {
        errorCause: OWNERSHIP_CHECK_FAILED
      });
    }
  }

  // In-App users are not allowed to access the bundle page and should be redirected to
  // the root page for recommendation engine to handle routing.
  if (isAppAuth) {
    Metrics.counter('bundle-page-redirected', {
      reason: 'in-app'
    }).increment();
    return /*#__PURE__*/_jsx(Redirect, {
      to: getRootUrl()
    });
  }
  return /*#__PURE__*/_jsxs("div", {
    "data-selenium-id": "page-success",
    children: [isAppReady && /*#__PURE__*/_jsx(NavMarker, {
      name: "BUNDLE_PAGE"
    }), /*#__PURE__*/_jsx(HeaderLayout, {
      selectedNavItem: 'bundle',
      children: /*#__PURE__*/_jsxs(ContentContainer, {
        children: [eligibleForHsfxDiscount ? /*#__PURE__*/_jsx(BannerSection, {
          screen: NavItemEnum.BUNDLE
        }) : /*#__PURE__*/_jsx(Spacer, {
          space: "400"
        }), /*#__PURE__*/_jsx(FailedLoadBanner, {
          hasFailed: hasPricingAndPurchasingFailed
        }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(PPUIPageHeader, {
          children: [/*#__PURE__*/_jsx(ProductPageTitle, {
            nonProductIdentifier: NavItemEnum.BUNDLE
          }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
            nonProductIdentifier: NavItemEnum.BUNDLE
          })]
        }), /*#__PURE__*/_jsx(PlanBuilderContainer, {
          children: /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsxs(Flex, {
              flexDirection: "column",
              flexGrow: 1,
              children: [/*#__PURE__*/_jsx(HubPlanPickerListContainer, {
                productLines: AllProductLines
              }), /*#__PURE__*/_jsx(AddonsPickerList, {
                defaultOpen: true
              })]
            }), /*#__PURE__*/_jsxs(RightColumnWrapper, {
              children: [/*#__PURE__*/_jsx(SummaryColumn, {
                shareLinkAnchor: "",
                hubsSignupProductOverride: "bundle"
              }), /*#__PURE__*/_jsx(PageSection, {
                children: /*#__PURE__*/_jsx(PhoneSupport, {
                  phoneSourceId: "bundle",
                  showBorder: false,
                  screen: SCREENS.BUNDLE,
                  upgradeProduct: BUNDLE
                })
              })]
            })]
          })
        })]
      })
    })]
  });
};
export default BundlePage;